import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';

import { supabase } from '@/integrations/supabase/client';

export type BackButtonAction =
  | 'hide-keyboard'
  | 'close-modal'
  | 'navigate-to-lists'
  | 'native-pop'
  | 'show-exit-hint'
  | 'exit-app'
  | 'native-exit'
  | 'history-fallback'
  | 'history-unavailable'
  | 'navigate-back'
  | 'navigate-fallback'
  | 'error';

export interface BackButtonLogPayload {
  action: BackButtonAction;
  route: string;
  modal: string | null;
  isKeyboardOpen: boolean;
  canGoBack: {
    event: boolean | null;
    client: boolean;
  };
  historyLength: number;
  doublePress: boolean;
  durationMs: number;
  metadata?: Record<string, unknown>;
  params?: Record<string, unknown>;
  networkStatus?: string | null;
  errorMessage?: string | null;
  errorDetails?: Record<string, unknown>;
}

interface PlatformContext {
  platform: string;
  appVersion: string | null;
  appBuild: string | null;
  osVersion: string | null;
  model: string | null;
  manufacturer: string | null;
  webViewVersion: string | null;
}

export type BackLogger = (payload: BackButtonLogPayload) => Promise<void> | void;

const isDev = Boolean(
  (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV
);

const sessionId = (() => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // ignore and fallback below
  }

  return `${Date.now().toString(36)}-${Math.random().toString(16).slice(2)}`;
})();

let platformContextPromise: Promise<PlatformContext> | null = null;

const loadPlatformContext = () => {
  if (!platformContextPromise) {
    platformContextPromise = (async () => {
      const platform = Capacitor.getPlatform?.() ?? 'unknown';

      let appVersion: string | null = null;
      let appBuild: string | null = null;
      try {
        const info = await App.getInfo();
        appVersion = info?.version ?? null;
        appBuild = info?.build ?? null;
      } catch {
        // App plugin non disponibile (es. web)
      }

      let osVersion: string | null = null;
      let model: string | null = null;
      let manufacturer: string | null = null;
      let webViewVersion: string | null = null;
      try {
        const info = await Device.getInfo();
        osVersion = info?.osVersion ?? null;
        model = info?.model ?? null;
        manufacturer = info?.manufacturer ?? null;
        webViewVersion = info?.webViewVersion ?? null;
      } catch {
        // Device plugin non disponibile
      }

      return {
        platform,
        appVersion,
        appBuild,
        osVersion,
        model,
        manufacturer,
        webViewVersion,
      } satisfies PlatformContext;
    })();
  }

  return platformContextPromise;
};

export const createBackLogger = (): BackLogger => {
  return async payload => {
    if (typeof fetch === 'undefined') {
      return;
    }

    const importMetaEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
    const supabaseUrl =
      importMetaEnv?.VITE_SUPABASE_URL ??
      (typeof process !== 'undefined' ? process.env?.VITE_SUPABASE_URL : undefined);
    const supabaseAnonKey =
      importMetaEnv?.VITE_SUPABASE_ANON_KEY ??
      (typeof process !== 'undefined' ? process.env?.VITE_SUPABASE_ANON_KEY : undefined);

    if (!supabaseUrl || !supabaseAnonKey) {
      if (isDev) {
        console.warn('[backLogger] Supabase environment not configured');
      }
      return;
    }

    const platformContext = await loadPlatformContext();

    let accessToken: string | null = null;
    try {
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        if (isDev) {
          console.warn('[backLogger] Unable to retrieve Supabase session', error);
        }
      } else {
        accessToken = data?.session?.access_token ?? null;
      }
    } catch (sessionError) {
      if (isDev) {
        console.warn('[backLogger] Supabase session lookup failed', sessionError);
      }
    }

    const clientTimestamp = new Date().toISOString();

    const requestContext = {
      modal: payload.modal,
      metadata: payload.metadata,
      isKeyboardOpen: payload.isKeyboardOpen,
      canGoBack: payload.canGoBack,
      platform: platformContext.platform,
      app: {
        version: platformContext.appVersion,
        build: platformContext.appBuild,
      },
      device: {
        osVersion: platformContext.osVersion,
        model: platformContext.model,
        manufacturer: platformContext.manufacturer,
        webViewVersion: platformContext.webViewVersion,
      },
    } satisfies Record<string, unknown>;

    const body = JSON.stringify({
      sessionId,
      clientTimestamp,
      route: payload.route,
      actionTaken: payload.action,
      canGoBackEvent: payload.canGoBack.event,
      canGoBackClient: payload.canGoBack.client,
      historyLength: payload.historyLength,
      hasModalOpen: !!payload.modal,
      doublePress: payload.doublePress,
      durationMs: Math.round(payload.durationMs),
      appVersion: platformContext.appVersion ?? undefined,
      platform: platformContext.platform ?? undefined,
      networkStatus: payload.networkStatus ?? undefined,
      params: payload.params ?? undefined,
      errorMessage: payload.errorMessage ?? undefined,
      errorDetails: payload.errorDetails ?? undefined,
      requestContext,
    });

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        apikey: supabaseAnonKey,
      };

      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      } else if (isDev) {
        console.warn('[backLogger] No Supabase session available, sending anonymous back event');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/log-back-event`, {
        method: 'POST',
        headers,
        body,
        keepalive: true,
      });

      if (!response.ok && isDev) {
        console.warn('[backLogger] Failed to log back event', response.status, await response.text());
      }
    } catch (error) {
      if (isDev) {
        console.warn('[backLogger] Error sending back event', error);
      }
    }
  };
};

export const backLogger = createBackLogger();
